/**
 * Selection + validation helpers for the Flexible Program "Import template"
 * modal (sc-46814). Pure, so they can be tested without React.
 *
 * The rules mirror msb-api's flexible-import-template (the source of truth):
 * a week with no workouts is empty, and so is a workout with no exercises.
 * Only the selected content is validated, so an untoggled empty week or
 * workout never blocks an import.
 *
 * Run the tests with: npm run test:import-template
 */
import moment from "moment";
import { addDays, daysBetween } from "./flexible-program";

export type TemplateSession = {
  order: number;
  name?: string;
  exerciseCount: number;
  setCount?: number;
  empty: boolean;
};
export type TemplateWeek = {
  week: number;
  empty: boolean;
  sessions: TemplateSession[];
};
export type TemplateBlock = {
  order: number;
  name?: string;
  weeks: TemplateWeek[];
};

/** `${blockOrder}:${week}` for an empty week, `${blockOrder}:${week}:${order}` for a workout. */
export type Selection = Record<string, true>;

export type InvalidItem = {
  blockOrder: number;
  blockName?: string;
  week: number;
  sessionOrder?: number;
  sessionName?: string;
  reason: "emptyWeek" | "emptyWorkout";
};

export type SelectionState = "all" | "some" | "none";

export const weekKey = (blockOrder: number, week: number) =>
  `${blockOrder}:${week}`;
export const sessionKey = (
  blockOrder: number,
  week: number,
  sessionOrder: number
) => `${blockOrder}:${week}:${sessionOrder}`;

/** The selectable keys of one week: its workouts, or the week itself if it has none. */
const weekKeys = (blockOrder: number, week: TemplateWeek) =>
  week.sessions.length
    ? week.sessions.map((session) =>
        sessionKey(blockOrder, week.week, session.order)
      )
    : [weekKey(blockOrder, week.week)];

const blockKeys = (block: TemplateBlock) =>
  block.weeks.flatMap((week) => weekKeys(block.order, week));

export const selectAll = (blocks: TemplateBlock[]): Selection =>
  Object.fromEntries(
    blocks.flatMap(blockKeys).map((key) => [key, true as const])
  );

export const selectNone = (): Selection => ({});

const stateOf = (keys: string[], selection: Selection): SelectionState => {
  const count = keys.filter((key) => selection[key]).length;
  if (!count) return "none";
  return count === keys.length ? "all" : "some";
};

export const blockState = (selection: Selection, block: TemplateBlock) =>
  stateOf(blockKeys(block), selection);

export const globalState = (selection: Selection, blocks: TemplateBlock[]) =>
  stateOf(blocks.flatMap(blockKeys), selection);

const setKeys = (
  selection: Selection,
  keys: string[],
  on: boolean
): Selection => {
  const next = { ...selection };
  for (const key of keys) {
    if (on) next[key] = true;
    else delete next[key];
  }
  return next;
};

/**
 * A block with anything selected (even partly, after deselecting its empty
 * items) is switched off in one click; only a block with nothing selected is
 * filled again.
 */
export const toggleBlock = (
  selection: Selection,
  block: TemplateBlock
): Selection =>
  setKeys(selection, blockKeys(block), blockState(selection, block) === "none");

export type SelectedWeek = {
  blockOrder: number;
  blockName?: string;
  week: number;
  sessions: TemplateSession[];
};

/** The weeks that would be imported, in template order. */
export const selectedWeeks = (
  blocks: TemplateBlock[],
  selection: Selection
): SelectedWeek[] =>
  blocks.flatMap((block) =>
    block.weeks
      .map((week) => ({
        blockOrder: block.order,
        blockName: block.name,
        week: week.week,
        sessions: week.sessions.filter(
          (session) =>
            selection[sessionKey(block.order, week.week, session.order)]
        ),
        selectedEmptyWeek:
          week.empty && selection[weekKey(block.order, week.week)],
      }))
      .filter((week) => week.sessions.length || week.selectedEmptyWeek)
      .map(({ selectedEmptyWeek: _ignored, ...week }) => week)
  );

export const selectedWeekCount = (
  blocks: TemplateBlock[],
  selection: Selection
) => selectedWeeks(blocks, selection).length;

export const selectedWorkoutCount = (
  blocks: TemplateBlock[],
  selection: Selection
) =>
  selectedWeeks(blocks, selection).reduce(
    (total, week) => total + week.sessions.length,
    0
  );

/** Same result shape as the API's `invalid`, computed on the selected content only. */
export const validateSelection = (
  blocks: TemplateBlock[],
  selection: Selection
): InvalidItem[] => {
  const invalid: InvalidItem[] = [];
  for (const week of selectedWeeks(blocks, selection)) {
    const base = {
      blockOrder: week.blockOrder,
      blockName: week.blockName,
      week: week.week,
    };
    if (!week.sessions.length) {
      invalid.push({ ...base, reason: "emptyWeek" });
      continue;
    }
    for (const session of week.sessions) {
      if (session.empty || !session.exerciseCount) {
        invalid.push({
          ...base,
          sessionOrder: session.order,
          sessionName: session.name,
          reason: "emptyWorkout",
        });
      }
    }
  }
  return invalid;
};

/**
 * The request's `selection`. `undefined` when everything is selected, so an
 * unchanged import sends exactly what the first version sent.
 */
export const toApiSelection = (
  blocks: TemplateBlock[],
  selection: Selection
) => {
  if (globalState(selection, blocks) === "all") return undefined;

  return blocks.flatMap((block) =>
    block.weeks.flatMap((week) => {
      if (week.empty) {
        return selection[weekKey(block.order, week.week)]
          ? [{ blockOrder: block.order, week: week.week }]
          : [];
      }
      const sessionOrders = week.sessions
        .filter(
          (session) =>
            selection[sessionKey(block.order, week.week, session.order)]
        )
        .map((session) => session.order);
      return sessionOrders.length
        ? [{ blockOrder: block.order, week: week.week, sessionOrders }]
        : [];
    })
  );
};

/** First and last imported week for the live "N weeks from X to Y" summary. */
export const importRange = (startDate: number, weekCount: number) => ({
  start: startDate,
  end: addDays(startDate, Math.max(weekCount, 1) * 7 - 1),
});

/**
 * Importing at the program's first week while others exist swaps the whole
 * program, so the modal asks for an explicit confirmation (`replaceAll`).
 */
export const willReplaceAll = (
  authoredStartDates: number[],
  startDate: number
) =>
  authoredStartDates.length > 1 &&
  Number(startDate) === Math.min(...authoredStartDates.map(Number));

/** Where the coach fixes an empty week or workout. */
export const templateEditorPath = (templateId: string, program = 1) =>
  `/coaching-portal/templates/edit-program/${templateId}?program=${program}`;

/** Program week number (1-based) of a lattice week start. */
export const programWeekNumber = (anchor: number, weekStart: number) =>
  daysBetween(anchor, weekStart) / 7 + 1;

export type BlockSummary = { weeks: number; workouts: number; sets: number };

/** What the block would bring, counting only workouts that have exercises. */
export const blockSummary = (block: TemplateBlock): BlockSummary => {
  const summary = { weeks: 0, workouts: 0, sets: 0 };
  for (const week of block.weeks) {
    const real = week.sessions.filter((session) => !session.empty);
    if (!real.length) continue;
    summary.weeks += 1;
    summary.workouts += real.length;
    summary.sets += real.reduce(
      (total, session) => total + (session.setCount || 0),
      0
    );
  }
  return summary;
};

export type Landing = {
  blockOrder: number;
  week: number;
  startDate: number;
  programWeek: number;
  workouts: number;
};

/** Where each selected week would land, back to back from the start week. */
export const planLanding = (
  blocks: TemplateBlock[],
  selection: Selection,
  startDate: number,
  anchor: number
): Landing[] =>
  selectedWeeks(blocks, selection).map((week, index) => {
    const date = addDays(startDate, index * 7);
    return {
      blockOrder: week.blockOrder,
      week: week.week,
      startDate: date,
      programWeek: programWeekNumber(anchor, date),
      workouts: week.sessions.length,
    };
  });

export type TargetWeek = { startDate: number; loggedWork?: boolean };

/** Landings that fall on a week with logged training, which can't be replaced. */
export const blockedLandings = (
  landings: Landing[],
  targetWeeks: TargetWeek[]
): Landing[] => {
  const logged = new Set(
    targetWeeks.filter((week) => week.loggedWork).map((week) => week.startDate)
  );
  return landings.filter((landing) => logged.has(landing.startDate));
};

/**
 * First start week at or after `from` whose whole range of `weekCount` weeks
 * clears every logged week, or null. Weeks past the known target list hold
 * nothing logged.
 */
export const firstClearStart = (
  targetWeeks: TargetWeek[],
  weekCount: number,
  from: number
): number | null => {
  if (weekCount < 1) return null;
  const logged = new Set(
    targetWeeks.filter((week) => week.loggedWork).map((week) => week.startDate)
  );
  const clear = (start: number) =>
    Array.from({ length: weekCount }).every(
      (_, index) => !logged.has(addDays(start, index * 7))
    );
  const found = targetWeeks.find(
    (week) => week.startDate >= from && clear(week.startDate)
  );
  return found ? found.startDate : null;
};

/** First and last landing of one block, or null when none of it is selected. */
export const blockLanding = (landings: Landing[], blockOrder: number) => {
  const mine = landings.filter((landing) => landing.blockOrder === blockOrder);
  return mine.length ? { first: mine[0], last: mine[mine.length - 1] } : null;
};

/** The empty items of the selection, grouped by block. */
export const groupInvalidByBlock = (invalid: InvalidItem[]) => {
  const groups = new Map<number, InvalidItem[]>();
  for (const item of invalid) {
    groups.set(item.blockOrder, [...(groups.get(item.blockOrder) || []), item]);
  }
  return groups;
};

/** Remove flagged weeks and workouts from the selection. */
export const deselectInvalid = (
  selection: Selection,
  items: InvalidItem[]
): Selection => {
  const next = { ...selection };
  for (const item of items) {
    if (item.reason === "emptyWorkout" && item.sessionOrder !== undefined) {
      delete next[sessionKey(item.blockOrder, item.week, item.sessionOrder)];
    } else {
      delete next[weekKey(item.blockOrder, item.week)];
    }
  }
  return next;
};

export type WeekNaming = {
  mode: "default" | "block" | "date" | "custom";
  pattern?: string;
};

export const NAMING_TOKENS = [
  "block",
  "week",
  "programWeek",
  "dates",
  "template",
] as const;

const MAX_WEEK_NAME_LENGTH = 60;

const prettyDate = (yyyymmdd: number) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D");

/** "Oct 29 – Nov 4" for the week starting on `startDate`. */
export const weekDateRange = (startDate: number) =>
  `${prettyDate(startDate)} – ${prettyDate(addDays(startDate, 6))}`;

/**
 * Name an imported week will get. Same rules as the API's buildWeekName
 * (msb-api flexible-import-template), so the timeline shows what is saved.
 */
export const buildWeekName = ({
  naming,
  templateName,
  blockName,
  blockOrder,
  week,
  programWeek,
  startDate,
  order,
}: {
  naming?: WeekNaming;
  templateName?: string;
  blockName?: string;
  blockOrder: number;
  week: number;
  programWeek: number;
  startDate: number;
  order: number;
}) => {
  const fallback = `Week ${order}`;
  const mode = naming?.mode || "default";
  const block = blockName || `Block ${blockOrder}`;

  let name = fallback;
  if (mode === "block") name = `${block} · Week ${week}`;
  else if (mode === "date") name = weekDateRange(startDate);
  else if (mode === "custom") {
    const tokens: Record<string, string | number> = {
      template: templateName || "",
      block,
      week,
      programWeek,
      dates: weekDateRange(startDate),
    };
    name = String(naming?.pattern || "").replace(
      /\{(template|block|week|programWeek|dates)\}/g,
      (_match, token) => String(tokens[token])
    );
  }
  return name.trim().slice(0, MAX_WEEK_NAME_LENGTH).trim() || fallback;
};

/** The request's `weekNames`; nothing for the default so old payloads stay as they were. */
export const toApiWeekNames = (naming: WeekNaming) =>
  naming.mode === "default"
    ? undefined
    : {
        mode: naming.mode,
        ...(naming.mode === "custom" ? { pattern: naming.pattern || "" } : {}),
      };
